import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import PhoneIcon from '@mui/icons-material/Phone';
import {
  Box,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useMemo, useState } from 'react';

import { DirectoryEntry } from '../../../@types/connect.type';
import { formatForDisplay } from '../../../utils/phone-format';
import { DirectoryEntryDialog } from '../../molecules/DirectoryEntryDialog';
import { useConnect } from '../ConnectProvider';

export const DirectoryPanel: React.FC = () => {
  const {
    directory,
    dial,
    addDirectoryEntry,
    updateDirectoryEntry,
    removeDirectoryEntry,
    initState,
  } = useConnect();
  const isReady = initState.status === 'ready';

  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<DirectoryEntry | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return directory;
    return directory.filter(
      (e) =>
        e.name.toLowerCase().includes(q) ||
        e.phoneNumber.toLowerCase().includes(q) ||
        (e.notes?.toLowerCase().includes(q) ?? false)
    );
  }, [search, directory]);

  const openAdd = () => {
    setEditing(null);
    setDialogOpen(true);
  };

  const openEdit = (entry: DirectoryEntry) => {
    setEditing(entry);
    setDialogOpen(true);
  };

  const handleSave = (data: { name: string; phoneNumber: string; notes: string | null }) => {
    if (editing) {
      updateDirectoryEntry(editing.id, data);
    } else {
      addDirectoryEntry(data);
    }
    setDialogOpen(false);
  };

  const onCall = async (entry: DirectoryEntry) => {
    try {
      await dial(entry.phoneNumber);
    } catch {
      // Errors surface in the dial panel; the directory row doesn't render
      // its own error slot to keep the list compact.
    }
  };

  return (
    <Stack sx={{ height: '100%' }}>
      <Box sx={{ p: 1, display: 'flex', gap: 1 }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Search contacts"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          inputProps={{ 'aria-label': 'Search directory' }}
        />
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={openAdd}
          sx={{ flexShrink: 0 }}
        >
          Add
        </Button>
      </Box>
      {filtered.length === 0 ? (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {directory.length === 0 ? 'No saved contacts yet.' : 'No matching contacts.'}
          </Typography>
        </Box>
      ) : (
        <List dense sx={{ flex: 1, overflowY: 'auto', py: 0 }}>
          {filtered.map((entry) => (
            <ListItem
              key={entry.id}
              divider
              secondaryAction={
                <Stack direction="row" spacing={0.5}>
                  <Tooltip title="Call">
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => onCall(entry)}
                        disabled={!isReady}
                        aria-label={`Call ${entry.name}`}
                      >
                        <PhoneIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Edit">
                    <IconButton
                      size="small"
                      onClick={() => openEdit(entry)}
                      aria-label={`Edit ${entry.name}`}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton
                      size="small"
                      onClick={() => removeDirectoryEntry(entry.id)}
                      aria-label={`Delete ${entry.name}`}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              }
            >
              <ListItemText
                primary={entry.name}
                secondary={
                  <>
                    <Typography component="span" variant="caption" color="text.primary">
                      {formatForDisplay(entry.phoneNumber)}
                    </Typography>
                    {entry.notes && (
                      <Typography
                        component="span"
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block' }}
                      >
                        {entry.notes}
                      </Typography>
                    )}
                  </>
                }
              />
            </ListItem>
          ))}
        </List>
      )}
      <DirectoryEntryDialog
        open={dialogOpen}
        initial={editing}
        onClose={() => setDialogOpen(false)}
        onSave={handleSave}
      />
    </Stack>
  );
};
